// Vulnerability Taxonomy Guard (front end)
// -----------------------------------------
// The pentest container owns the vulnerability taxonomy and can ship a new
// category, name or severity ahead of every mirror of it — this app's
// display names, the backend's enums, the GraphQL schema. That drift used to
// be catastrophic: AppSync enum coercion is fail-closed for the entire
// response, so one unknown value nulled every finding of every test in the
// query. The read types now carry String (see the note on `type
// Vulnerabilities` in the schema), which moves the decision here, to the
// surface that has to draw the thing.
//
// The rule this module implements: render what we can understand, drop what
// we cannot, and never let one unmappable finding take the others with it.
//
// Category and name are display-only — an unmirrored value costs a
// title-cased label (formatVulnCategory handles it) and nothing else, so
// those rows are KEPT.
//
// riskRating is different. Every surface keys off it: the severity tiles
// count it, the filter pills filter on it, sorting orders by it, and the
// report groups by it. A value none of them recognise produces a row that
// is in the table but in no tile and under no filter — "All 23" above a set
// of tiles summing to 22, with no way to reach the missing one. That row is
// dropped, and dropping it is logged loudly.
//
// The authoritative alarm lives on the backend, which logs unmirrored values
// as each finding is recorded during the test (models/vuln_taxonomy.py).
// This is the client-side half: it keeps the UI coherent, and says so.

import {
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  RISK_RATING_UNCLASSIFIED,
} from "@/constants/definitions";

// Mirrors the backend RiskLevel enum. Anything outside this set cannot be
// counted, filtered or sorted by any surface in the app.
const KNOWN_RISK_RATINGS: ReadonlySet<string> = new Set([
  RISK_RATING_UNCLASSIFIED,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  RISK_RATING_HIGH,
  RISK_RATING_CRITICAL,
]);

// Minimal shape this guard needs. Deliberately not VulnerabilityClass: the
// live discovery stream's slim rows pass through here too, and typing to the
// structural minimum keeps the model free to change around it.
interface TaxonomyBearingFinding {
  id?: string;
  reference?: string;
  riskRating?: string;
}

// One log line per unrecognised severity per session. Without this, a value
// the backend starts emitting would print on every render of every table —
// thousands of identical lines that bury the one that matters.
const alreadyReportedRiskRatings = new Set<string>();

export function isDisplayableFinding(finding: TaxonomyBearingFinding): boolean {
  return !!finding?.riskRating && KNOWN_RISK_RATINGS.has(finding.riskRating);
}

/**
 * Keep the findings every surface can agree on; drop and report the rest.
 *
 * Returns the input array unchanged (same reference) when nothing needs
 * dropping — the overwhelmingly common case, and it keeps callers that
 * compare references cheap.
 *
 * @param findings the candidate rows, in display order
 * @param source   where this ran, for the log line (e.g. a test id)
 */
export function selectDisplayableFindings<T extends TaxonomyBearingFinding>(
  findings: T[],
  source: string,
): T[] {
  if (!findings?.length) return findings ?? [];

  const displayable = findings.filter(isDisplayableFinding);
  if (displayable.length === findings.length) return findings;

  const dropped = findings.filter((finding) => !isDisplayableFinding(finding));
  const unreportedRatings = dropped
    .map((finding) => finding.riskRating || "(none)")
    .filter((riskRating) => !alreadyReportedRiskRatings.has(riskRating));

  if (unreportedRatings.length > 0) {
    unreportedRatings.forEach((riskRating) =>
      alreadyReportedRiskRatings.add(riskRating),
    );
    console.error(
      `Dropped ${dropped.length} finding(s) from ${source}: risk rating(s) ` +
        `${Array.from(new Set(unreportedRatings)).join(", ")} are not in this ` +
        `build's severity taxonomy, so they could not be counted, filtered or ` +
        `sorted. The remaining findings are shown. References: ` +
        `${dropped.map((finding) => finding.reference || finding.id).join(", ")}. ` +
        `Mirror the value in the backend RiskLevel enum, the GraphQL schema and ` +
        `the dashboard's risk-rating constants.`,
    );
  }

  return displayable;
}

// Test seam — the dedup set is module state that would otherwise leak
// between test cases.
export function resetTaxonomyReportingForTests(): void {
  alreadyReportedRiskRatings.clear();
}
